/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const FEATURES = [
  {
    icon: '🎯',
    title: 'Your Skills, Captured',
    body: "Every domain you've mastered, surfaced into a footprint you can actually show."
  },
  {
    icon: '🤖',
    title: 'SkillBot Access',
    body: 'Ask anything about your domain — and every question becomes part of your knowledge contribution.'
  },
  {
    icon: '🌟',
    title: 'Goal-driven Discovery',
    body: 'AI surfaces opportunities and people that match where you want to go.'
  },
  {
    icon: '🤝',
    title: 'Living Reputation',
    body: 'Every connection, every contribution, every project becomes a verified outcome.'
  }
];

const DEMO_MAILTO =
  'mailto:info@skilldelivery.co.in?subject=Early%20access%20-%20Individual';

const IndividualLanding = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">

      <PublicNav />

      {/* ─── Decorative radial glows ────────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-10">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            For Individuals
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Your expertise. Visible.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            A platform that captures what you actually know — and reflects it back to you, your peers, and the world that needed to know you all along.
          </p>
          <p className="mt-4 text-sm italic text-brand-text2 max-w-2xl mx-auto">
            Skills, Delivered!
          </p>
        </header>

        {/* ── Feature grid ── */}
        <section
          aria-label="What you get"
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                <span className="text-2xl leading-none" aria-hidden="true">{f.icon}</span>
              </div>
              <h3 className="font-bold text-base text-brand-text">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </section>

        {/* ── Manifesto callout ── */}
        <section className="mt-16 text-center">
          <p className="text-sm text-brand-muted">
            <Link
              to="/manifesto"
              className="text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
            >
              Read why SkillDelivery exists →
            </Link>
          </p>
        </section>

        {/* ── Final CTA ── */}
        <section className="mt-12 text-center">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Ready to become visible?
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
            Start showing the world who you are.
          </h2>
          <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Free to start. Built for individual practitioners across WMS, iSeries, Civil, Apparel, and ERPNext.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Sign up free →
            </Link>
            <Link
              to="/tour"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Take the platform tour
            </Link>
            <a
              href={DEMO_MAILTO}
              className="inline-flex items-center justify-center px-6 py-3 text-brand-muted hover:text-brand-text font-medium hover:underline transition-colors"
            >
              Or talk to Lalit
            </a>
          </div>
        </section>

        {/* ── Inline footer ── */}
        <footer className="text-center mt-16">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            ← Back to home
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default IndividualLanding;
